import { osnovaApi } from '../base';
import type {
  KadryAssignee,
  KadryAssigneeRole,
  KadryAuditQuery,
  KadryAuditRecord,
  KadryCreateRequest,
  KadryGate,
  KadryAutoofferSettings,
  KadryAutoofferSettingsBody,
  KadryGateState,
  KadryHiringRequest,
  KadryInfo,
  KadryListRequestsQuery,
  KadryNewProfileDraft,
  KadryPatchRequest,
  KadryProfileVersion,
  KadrySearchProfile,
  KadrySetGateBody,
  KadryStaffMember,
  KadryStatusHistoryEntry,
  KadryWorkSite,
  KadryWorkSiteInput,
} from '@entities/kadry/types';

const BASE = '/agents/kadry';

/** Заголовок `If-Match` с версией объекта (ADR-0019 §8). */
const ifMatch = (version: number) => ({ 'If-Match': `"${version}"` });

/** Размер страницы журнала аудита (курсор `before_id`). */
export const KADRY_AUDIT_PAGE_SIZE = 50;

/** Фильтры журнала без курсора — курсор ведёт бесконечный запрос. */
export type KadryAuditFilter = Omit<KadryAuditQuery, 'before_id' | 'limit'>;

/**
 * ОСНОВА.Кадры — `/api/v1/agents/kadry/*` (контракт: docs/kadry/openapi.json).
 *
 * Инвалидация: изменения заявки обновляют её карточку, реестр и историю;
 * утверждение профиля меняет и заявку (активная версия, `version`). Любое
 * изменение пишет событие аудита — журнал тоже инвалидируется. На ошибке
 * (в т.ч. `version_conflict`) кэш не трогаем: обновление — по кнопке.
 */
export const kadryApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Состояние модуля, роли и доступные операции текущего пользователя. */
    getKadryInfo: b.query<KadryInfo, void>({
      query: () => `${BASE}/info`,
      providesTags: ['KadryInfo'],
    }),

    // ===== Заявки =====

    listKadryRequests: b.query<KadryHiringRequest[], KadryListRequestsQuery>({
      query: (params) => ({ url: `${BASE}/requests`, params }),
      providesTags: (res) => [
        { type: 'KadryRequest', id: 'LIST' },
        ...(res ?? []).map((r) => ({ type: 'KadryRequest' as const, id: r.id })),
      ],
    }),
    getKadryRequest: b.query<KadryHiringRequest, string>({
      query: (id) => `${BASE}/requests/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'KadryRequest', id }],
    }),
    createKadryRequest: b.mutation<KadryHiringRequest, KadryCreateRequest>({
      query: (body) => ({ url: `${BASE}/requests`, method: 'POST', body }),
      invalidatesTags: (r) => (r ? [{ type: 'KadryRequest', id: 'LIST' }, 'KadryAudit'] : []),
    }),
    /** Правка полей и/или переход статуса (If-Match: версия заявки). */
    patchKadryRequest: b.mutation<
      KadryHiringRequest,
      { id: string; version: number; patch: KadryPatchRequest }
    >({
      query: ({ id, version, patch }) => ({
        url: `${BASE}/requests/${id}`,
        method: 'PATCH',
        body: patch,
        headers: ifMatch(version),
      }),
      invalidatesTags: (r, _e, { id }) =>
        r
          ? [
              { type: 'KadryRequest', id },
              { type: 'KadryRequest', id: 'LIST' },
              { type: 'KadryRequestHistory', id },
              'KadryAudit',
            ]
          : [],
    }),
    getKadryRequestHistory: b.query<KadryStatusHistoryEntry[], string>({
      query: (id) => `${BASE}/requests/${id}/history`,
      providesTags: (_r, _e, id) => [{ type: 'KadryRequestHistory', id }],
    }),
    assignKadryRequest: b.mutation<
      KadryAssignee[],
      { id: string; user_id: string; role: KadryAssigneeRole }
    >({
      query: ({ id, user_id, role }) => ({
        url: `${BASE}/requests/${id}/assignees`,
        method: 'POST',
        body: { user_id, role },
      }),
      invalidatesTags: (r, _e, { id }) =>
        r
          ? [{ type: 'KadryRequest', id }, { type: 'KadryRequest', id: 'LIST' }, 'KadryAudit']
          : [],
    }),
    unassignKadryRequest: b.mutation<KadryAssignee[], { id: string; user_id: string }>({
      query: ({ id, user_id }) => ({
        url: `${BASE}/requests/${id}/assignees/${user_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (r, _e, { id }) =>
        r
          ? [{ type: 'KadryRequest', id }, { type: 'KadryRequest', id: 'LIST' }, 'KadryAudit']
          : [],
    }),

    // ===== Профили поиска =====

    listKadryProfiles: b.query<KadryProfileVersion[], string>({
      query: (requestId) => `${BASE}/requests/${requestId}/profiles`,
      providesTags: (_r, _e, requestId) => [{ type: 'KadryProfile', id: requestId }],
    }),
    getKadryProfile: b.query<KadryProfileVersion, { requestId: string; version: number }>({
      query: ({ requestId, version }) => `${BASE}/requests/${requestId}/profiles/${version}`,
      providesTags: (_r, _e, { requestId }) => [{ type: 'KadryProfile', id: requestId }],
    }),
    /** Черновик: вручную (готовый профиль) или копией утверждённой версии. */
    createKadryProfileDraft: b.mutation<
      KadryProfileVersion,
      { requestId: string; body: KadryNewProfileDraft }
    >({
      query: ({ requestId, body }) => ({
        url: `${BASE}/requests/${requestId}/profiles`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (r, _e, { requestId }) =>
        r ? [{ type: 'KadryProfile', id: requestId }, 'KadryAudit'] : [],
    }),
    /** Правка черновика (If-Match: ревизия черновика). */
    updateKadryProfile: b.mutation<
      KadryProfileVersion,
      { requestId: string; version: number; revision: number; profile: KadrySearchProfile }
    >({
      query: ({ requestId, version, revision, profile }) => ({
        url: `${BASE}/requests/${requestId}/profiles/${version}`,
        method: 'PUT',
        body: profile,
        headers: ifMatch(revision),
      }),
      invalidatesTags: (r, _e, { requestId }) =>
        r ? [{ type: 'KadryProfile', id: requestId }, 'KadryAudit'] : [],
    }),
    /** Утверждение версии: она становится неизменяемой и активной для заявки. */
    approveKadryProfile: b.mutation<
      KadryProfileVersion,
      { requestId: string; version: number; revision: number }
    >({
      query: ({ requestId, version, revision }) => ({
        url: `${BASE}/requests/${requestId}/profiles/${version}/approve`,
        method: 'POST',
        headers: ifMatch(revision),
      }),
      invalidatesTags: (r, _e, { requestId }) =>
        r
          ? [
              { type: 'KadryProfile', id: requestId },
              { type: 'KadryRequest', id: requestId },
              { type: 'KadryRequest', id: 'LIST' },
              'KadryAudit',
            ]
          : [],
    }),

    // ===== Справочник объектов =====

    listKadryWorkSites: b.query<KadryWorkSite[], { include_inactive?: boolean } | void>({
      query: (params) => ({ url: `${BASE}/work-sites`, params: params ?? undefined }),
      providesTags: [{ type: 'KadryWorkSite', id: 'LIST' }],
    }),
    createKadryWorkSite: b.mutation<KadryWorkSite, KadryWorkSiteInput>({
      query: (body) => ({ url: `${BASE}/work-sites`, method: 'POST', body }),
      invalidatesTags: (r) => (r ? [{ type: 'KadryWorkSite', id: 'LIST' }, 'KadryAudit'] : []),
    }),
    /** Правка объекта (If-Match: версия). PUT заменяет объект целиком. */
    updateKadryWorkSite: b.mutation<
      KadryWorkSite,
      { id: string; version: number; body: KadryWorkSiteInput }
    >({
      query: ({ id, version, body }) => ({
        url: `${BASE}/work-sites/${id}`,
        method: 'PUT',
        body,
        headers: ifMatch(version),
      }),
      invalidatesTags: (r) => (r ? [{ type: 'KadryWorkSite', id: 'LIST' }, 'KadryAudit'] : []),
    }),

    // ===== Автопредложения =====

    /** Порог отбора и суточные лимиты автопредложений (`hr_admin`). */
    getKadryAutoofferSettings: b.query<KadryAutoofferSettings, void>({
      query: () => `${BASE}/autooffer-settings`,
      providesTags: [{ type: 'KadryGate', id: 'AUTOOFFER' }],
    }),
    /** Сохранение настроек. `version` из чтения — 409, если кто-то правил раньше. */
    setKadryAutoofferSettings: b.mutation<KadryAutoofferSettings, KadryAutoofferSettingsBody>({
      query: (body) => ({
        url: `${BASE}/autooffer-settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (r) => (r ? [{ type: 'KadryGate', id: 'AUTOOFFER' }, 'KadryAudit'] : []),
    }),

    // ===== Фичегейты =====

    listKadryGates: b.query<KadryGateState[], void>({
      query: () => `${BASE}/feature-gates`,
      providesTags: [{ type: 'KadryGate', id: 'LIST' }],
    }),
    /** Переключение гейта (`hr_admin`, If-Match: версия гейта). */
    setKadryGate: b.mutation<
      KadryGateState,
      { gate: KadryGate; version: number; body: KadrySetGateBody }
    >({
      query: ({ gate, version, body }) => ({
        url: `${BASE}/feature-gates/${encodeURIComponent(gate)}`,
        method: 'PUT',
        body,
        headers: ifMatch(version),
      }),
      invalidatesTags: (r) => (r ? [{ type: 'KadryGate', id: 'LIST' }, 'KadryAudit'] : []),
    }),

    // ===== Журнал аудита =====

    /** Журнал (без ПДн), новые сверху; «ещё» — курсор `before_id` (id последней записи). */
    listKadryAudit: b.infiniteQuery<KadryAuditRecord[], KadryAuditFilter, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) =>
          lastPage.length < KADRY_AUDIT_PAGE_SIZE ? undefined : lastPage[lastPage.length - 1].id,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${BASE}/audit`,
        params: {
          ...queryArg,
          before_id: pageParam > 0 ? pageParam : undefined,
          limit: KADRY_AUDIT_PAGE_SIZE,
        },
      }),
      providesTags: ['KadryAudit'],
    }),

    // ===== Справочник сотрудников =====

    /** Активные сотрудники для назначения на заявку в роли `role`. */
    listKadryStaff: b.query<KadryStaffMember[], KadryAssigneeRole>({
      query: (role) => ({ url: `${BASE}/staff`, params: { role } }),
      providesTags: (_r, _e, role) => [{ type: 'KadryStaff', id: role }],
    }),
  }),
});

export const {
  useGetKadryInfoQuery,
  useListKadryRequestsQuery,
  useGetKadryRequestQuery,
  useCreateKadryRequestMutation,
  usePatchKadryRequestMutation,
  useGetKadryRequestHistoryQuery,
  useAssignKadryRequestMutation,
  useUnassignKadryRequestMutation,
  useListKadryProfilesQuery,
  useGetKadryProfileQuery,
  useCreateKadryProfileDraftMutation,
  useUpdateKadryProfileMutation,
  useApproveKadryProfileMutation,
  useListKadryWorkSitesQuery,
  useCreateKadryWorkSiteMutation,
  useUpdateKadryWorkSiteMutation,
  useGetKadryAutoofferSettingsQuery,
  useSetKadryAutoofferSettingsMutation,
  useListKadryGatesQuery,
  useSetKadryGateMutation,
  useListKadryAuditInfiniteQuery,
  useListKadryStaffQuery,
} = kadryApi;
